import { ref, onMounted, onUnmounted } from 'vue'

export function useInteractionBasedReload(hoursThreshold = 2) {
    const thresholdMs = hoursThreshold * 60 * 60 * 1000
    const storageKey = 'lastUserInteraction'

    // Reactive state
    const lastInteractionTime = ref(0)
    const timeSinceLastInteraction = ref(0)

    // Event listeners array to track for cleanup
    const eventListeners = []

    // Update timestamp on any user interaction
    const updateLastInteractionTime = () => {
        const currentTime = Date.now()
        localStorage.setItem(storageKey, currentTime.toString())
        lastInteractionTime.value = currentTime
        updateTimeSinceLastInteraction()
    }

    // Update reactive time difference
    const updateTimeSinceLastInteraction = () => {
        if (lastInteractionTime.value) {
            timeSinceLastInteraction.value = Date.now() - lastInteractionTime.value
        }
    }

    // Check if threshold has passed and reload if needed
    const checkAndReloadIfNeeded = () => {
        const lastInteraction = parseInt(localStorage.getItem(storageKey) || '0')
        const currentTime = Date.now()
        const timeSinceLastInteraction = currentTime - lastInteraction

        if (lastInteraction && timeSinceLastInteraction >= thresholdMs) {
            // Update timestamp before reload to prevent reload loops
            updateLastInteractionTime()
            window.location.reload()
            return true
        }

        return false
    }

    // Get formatted time since last interaction
    const getFormattedTimeSince = () => {
        if (!lastInteractionTime.value) return null

        const timeDiff = Date.now() - lastInteractionTime.value
        const minutes = Math.floor(timeDiff / (1000 * 60))
        const hours = Math.floor(minutes / 60)

        return {
            milliseconds: timeDiff,
            minutes: minutes,
            hours: hours,
            formatted: hours > 0 ? `${hours}h ${minutes % 60}m` : `${minutes}m`
        }
    }

    // Reset interaction timer (for testing)
    const resetInteractionTimer = () => {
        localStorage.removeItem(storageKey)
        lastInteractionTime.value = 0
        timeSinceLastInteraction.value = 0
    }

    // Setup event listeners
    const setupEventListeners = () => {
        const interactionEvents = [
            'click',
            'keydown',
            'scroll',
            'mousemove',
            'touchstart',
            'input',
            'focus'
        ]

        // Add interaction event listeners
        interactionEvents.forEach(eventType => {
            const handler = () => {
                if (!checkAndReloadIfNeeded()) {
                    updateLastInteractionTime()
                }
            }

            document.addEventListener(eventType, handler, { passive: true })
            eventListeners.push({ element: document, event: eventType, handler })
        })

        // Visibility change handler
        const visibilityHandler = () => {
            if (!document.hidden) {
                checkAndReloadIfNeeded()
            }
        }
        document.addEventListener('visibilitychange', visibilityHandler)
        eventListeners.push({ element: document, event: 'visibilitychange', handler: visibilityHandler })

        // Window focus handler
        const focusHandler = () => {
            checkAndReloadIfNeeded()
        }
        window.addEventListener('focus', focusHandler)
        eventListeners.push({ element: window, event: 'focus', handler: focusHandler })
    }

    // Cleanup event listeners
    const cleanupEventListeners = () => {
        eventListeners.forEach(({ element, event, handler }) => {
            element?.removeEventListener(event, handler)
        })
        eventListeners.length = 0
    }

    // Initialize on mount
    onMounted(() => {
        // Load existing timestamp or create new one
        const existingTimestamp = localStorage.getItem(storageKey)
        if (existingTimestamp) {
            lastInteractionTime.value = parseInt(existingTimestamp)
            updateTimeSinceLastInteraction()

            const minutesAgo = Math.round((Date.now() - lastInteractionTime.value) / (1000 * 60))
        } else {
            updateLastInteractionTime()
        }

        setupEventListeners()

        // Optional: Update time display every minute
        const updateInterval = setInterval(updateTimeSinceLastInteraction, 60000)

        // Store interval ID for cleanup
        eventListeners.push({ cleanup: () => clearInterval(updateInterval) })
    })

    // Cleanup on unmount
    onUnmounted(() => {
        cleanupEventListeners()

        // Clean up any additional cleanup functions
        eventListeners.forEach(listener => {
            if (listener.cleanup) {
                listener.cleanup()
            }
        })
    })

    return {
        lastInteractionTime: lastInteractionTime,
        timeSinceLastInteraction: timeSinceLastInteraction,
        getFormattedTimeSince,
        resetInteractionTimer,
        updateLastInteractionTime
    }
}
